
import { Game } from "@/models/Game";
import { DateTime } from "luxon";
import { Component, Vue, Prop } from "vue-property-decorator";
import SavePanel from "../../Base/SavePanel.vue";
import GameStatus from "../../Base/GameStatus.vue";
import CloneGame from "../ComposeTabs/ComposeAdd/CloneGame.vue";
import DeleteBox from "../../Base/DeleteBox.vue";
import DateHelper from "@/utils/DateHelper";
import { ManhuntMapPoi } from "@/models/MapPois/ManHunt/ManhuntMapPoi";
import { UserData } from "@/models/User";
import { forIn } from "lodash";
import { City } from "@/models/City";

@Component({
  components: {
    SavePanel,
    GameStatus,
    CloneGame,
    DeleteBox,
  },
})
class ComposePublish extends Vue {
  @Prop({ default: null }) readonly gameData!: Game;
  @Prop({ default: null }) readonly timezone!: any;
  @Prop({ default: false }) readonly isEditingLocked!: boolean;
  showSavePanel = false;
  showCloneGame = false;
  errorMessage = "";

  get getGameParentClone(): string {
    if (
      this.gameData.clone_parent !== undefined &&
      this.gameData.clone_parent.length > 0
    ) {
      return this.gameData.clone_parent;
    } else {
      return "N/A";
    }
  }

  get isAdmin(): boolean {
    return this.$store.getters["authenication/isAdmin"];
  }

  get getTeamCount(): number {
    if (this.gameData.team_count !== undefined) {
      return this.gameData.team_count;
    } else {
      return 0;
    }
  }

  get getStartDateInShortFormat(): string {
    return (
      DateTime.fromISO(this.gameData.start_dt, {
        zone: this.getCityData.tZone?.timeZoneId,
      }).toFormat("dd MMMM yyyy") +
      ", " +
      DateTime.fromISO(this.gameData.start_dt, {
        zone: this.getCityData.tZone?.timeZoneId,
      }).toFormat("HH:mm")
    );
  }
  get getUserData(): UserData {
    return this.$store.state.authenication.userData;
  }
  get getPreAppLaunch(): number {
    return this.$store.state.authenication.appPrelaunch._value;
  }
  get canEditDate(): boolean {
    var results = DateHelper.getStatus(this.gameData, this.getPreAppLaunch);
    if (results === "OPEN") {
      return true;
    }
    return false;
  }
  get getEndDateInShortFormat(): string {
    return (
      DateTime.fromISO(this.gameData.end_dt, {
        zone: this.getCityData.tZone?.timeZoneId,
      }).toFormat("dd MMMM yyyy") +
      ", " +
      DateTime.fromISO(this.gameData.end_dt, {
        zone: this.getCityData.tZone?.timeZoneId,
      }).toFormat("HH:mm")
    );
  }
  get getMapPoiData(): ManhuntMapPoi[] {
    return this.$store.state.mapPoiStore.mapPoi;
  }

  get getCityData(): City {
    return this.$store.state.cityStore.singleCity;
  }

  get getTimeZone(): string {
    if (this.getCityData.tZone === undefined) {
      return "Invalid";
    } else {
      return (
        this.getCityData.tZone.timeZoneId +
        ", " +
        this.getCityData.tZone.timeZoneName +
        " (" +
        DateTime.fromISO(this.gameData.start_dt, {
          zone: this.getCityData.tZone!.timeZoneId,
        }).offset /
          60 +
        ")"
      );
    }
  }

  get showDraftButton(): boolean {
    const status = DateHelper.getStatus(this.gameData, this.getPreAppLaunch);
    if (
      status === "LIVE" ||
      status === "PUBLISHED" ||
      status === "OPEN" ||
      status == "ENDED"
    ) {
      return true;
    } else {
      return false;
    }
  }

  get getWarningArray(): string[] {
    const arr: string[] = [];
    forIn(this.gameData.issues?.warn, (val: any) => {
      arr.push(val.txt.en);
    });
    return arr;
  }

  get generateLink(): string {
    return process.env.VUE_APP_DATABASEURL + "/games/" + this.gameData.key;
  }

  get getErrorArray(): string[] {
    const arr: string[] = [];
    forIn(this.gameData.issues?.err, (val: any) => {
      arr.push(val.txt.en);
    });
    return arr;
  }

  get showCloneGameButton(): boolean {
    if (this.gameData.gt_uid === "manhunt") {
      return false;
    }
    const status = DateHelper.getStatus(this.gameData, this.getPreAppLaunch);
    if (
      status === "WIP" ||
      status === "PURGING" ||
      status === "ENDED" ||
      status === "TEMPLATE" ||
      status === "PUBLISHED"
    ) {
      return true;
    } else {
      return false;
    }
  }
  get showTemplateButton(): boolean {
    if (this.gameData.evergreen?.isClone) {
      return false;
    }
    const status = DateHelper.getStatus(this.gameData, this.getPreAppLaunch);
    if (status === "WIP") {
      return true;
    } else {
      return false;
    }
  }

  get showPublishButton(): boolean {
    const status = DateHelper.getStatus(this.gameData, this.getPreAppLaunch);
    if (status === "WIP") {
      return true;
    } else {
      return false;
    }
  }

  showTemplate(): void {
    this.$bvModal.show("modal-template");
  }
  closeTemplate(): void {
    this.$bvModal.hide("modal-template");
  }
  showPublish(): void {
    this.$bvModal.show("modal-publish");
  }
  closeDraft(): void {
    this.$bvModal.hide("modal-draft");
  }
  showDraft(): void {
    this.$bvModal.show("modal-draft");
  }
  closePublish(): void {
    this.$bvModal.hide("modal-publish");
  }
  makeTemplate(): void {
    this.$store.dispatch("gameStore/makeTemplate", this.gameData.key);
    this.$bvModal.hide("modal-template");
  }
  makePublish(): void {
    this.$store.dispatch("gameStore/makePublish", this.gameData.key);
    this.$bvModal.hide("modal-publish");
  }
  makeDraft(): void {
    this.$store.dispatch("gameStore/makeDraft", this.gameData.key);
    this.$bvModal.hide("modal-draft");
  }
  cloneGame(): void {
    this.showCloneGame = true;
  }
  closeDialog(): void {
    this.showCloneGame = false;
  }
  openModal(): void {
    this.$bvModal.show("modal-voucher-refresh");
  }
  refreshVoucher(): void {
    this.$store
      .dispatch("toolBoxStore/refresh_voucher", this.gameData.key)
      .then((result) => {
        if (result.data.status === "OK") {
          this.$bvModal.hide("modal-voucher-refresh");
        } else {
          this.errorMessage =
            "Something went wrong. Please contact the administator";
        }
      })
      .catch((error) => {
        this.errorMessage = error.message;
      });
  }
  closeModal(): void {
    this.$bvModal.hide("modal-voucher-refresh");
  }
  openDeleteBox(): void {
    this.$bvModal.show("team-modal-delete");
  }
  deleteItem(): void {
    this.gameData.start_dt = DateTime.now()
      .minus({ days: 2 })
      .toISO();
    this.gameData.end_dt = DateTime.now()
      .minus({ days: 2 })
      .toISO();
    this.$store.dispatch("gameStore/deleteGame", this.gameData);
  }
  closeDialogLanguage(): void {
    this.$emit("closeDialogLanguage");
  }

  handlelock(): void {
    this.$emit("handlelock");
  }
}

export default ComposePublish;
